import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { FieldDetails } from '@openx/components-core/lib/FieldDetails/FieldDetails.tsx';
import { ACL_TYPE } from '@openx/types/aclType.ts';
import { ThirdPartyFeeConfigRevenueMethod } from '@openx/types/gqlTypes.ts';
import { microToPercentWithPrecision } from '@openx/utils/lib/microToPercent.ts';
import { priceFormat } from '@openx/utils/lib/priceFormat.ts';
import { toPercentString } from '@openx/utils/lib/toPercentString.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators.ts';
import { useTranslation } from 'react-i18next';
import { grossFeeTooltip, netFeeTooltip } from '../../../../components/CuratedDealFees/constants.ts';
import { calculateNetFee } from '../../../../DealForm/sections/CuratedDealFeesSection/utils/calculateNetFee.ts';
import { usePartnersFetch } from '../hooks/usePartnersFetch.ts';

type ThirdPartyFeeDetailsProps = {
  accountUid: string;
  currency: string;
  grossCpmCap: string | null;
  grossShare: string | null;
  historyMode?: boolean;
  isDirectDeal?: boolean;
  partnerId: string;
  platformShare: string | null;
  revenueMethod: ThirdPartyFeeConfigRevenueMethod;
};

export const ThirdPartyFeeDetails = ({
  accountUid,
  currency,
  grossCpmCap,
  grossShare,
  historyMode,
  isDirectDeal,
  partnerId,
  platformShare,
  revenueMethod,
}: ThirdPartyFeeDetailsProps) => {
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();
  const { t } = useTranslation();

  const { mapById: partnersById, loading: partnersLoading } = usePartnersFetch(accountUid, isDirectDeal);

  const formattedGrossCpmCap = grossCpmCap ? priceFormat(grossCpmCap, currency) : undefined;
  const formattedGrossShare = toPercentString(grossShare);
  const grossSharePercent = microToPercentWithPrecision(grossShare);
  const platformSharePercent = microToPercentWithPrecision(platformShare) ?? '';
  const hasReadPermission = isAllowed(isAllowedByCompiledAcl(ACL_TYPE.THIRD_PARTY_FEES_PLATFORM_SHARE_READ));
  const isCpmRevenue = revenueMethod === ThirdPartyFeeConfigRevenueMethod.Cpm;
  const netFee = isCpmRevenue
    ? calculateNetFee(grossCpmCap ?? '', platformSharePercent, 2)
    : calculateNetFee(grossSharePercent ?? '', platformSharePercent, 1);

  return (
    <>
      <Typography
        data-test="oa-access-partner-heading"
        variant={historyMode ? 'body1' : 'h3'}
        sx={{
          my: 2,
        }}
      >
        {t('Partner Fee')}
      </Typography>
      <Grid container>
        <Grid size={3}>
          <FieldDetails title={t('Partner ID')} value={partnerId} />
        </Grid>

        <Grid size="grow">
          <FieldDetails loading={partnersLoading} title={t('Partner')} value={partnersById[partnerId]?.name} />
        </Grid>
      </Grid>
      {!isDirectDeal && (
        <>
          <Typography
            data-test="oa-access-fee-heading"
            variant={historyMode ? 'body1' : 'h3'}
            sx={{
              my: 2,
            }}
          >
            {isCpmRevenue ? t('Fixed CPM') : t('Percentage of Media')}
          </Typography>
          <Grid container>
            <Grid size={3}>
              <FieldDetails
                hideTitle={!hasReadPermission}
                title={t('Gross Fee')}
                tooltipIconText={isCpmRevenue ? t(grossFeeTooltip.cpm) : t(grossFeeTooltip.pom)}
                value={isCpmRevenue ? formattedGrossCpmCap : formattedGrossShare}
              />
            </Grid>

            {hasReadPermission && (
              <Grid size="grow">
                <FieldDetails
                  title={t('Net Fee')}
                  tooltipIconText={isCpmRevenue ? t(netFeeTooltip.cpm) : t(netFeeTooltip.pom)}
                  value={isCpmRevenue ? priceFormat(netFee, currency) : `${netFee}%`}
                />
              </Grid>
            )}
          </Grid>
          {hasReadPermission && (
            <>
              <Typography
                data-test="oa-access-partner-heading"
                variant={historyMode ? 'body1' : 'h3'}
                sx={{
                  my: 2,
                }}
              >
                {t('OpenX Platform Fee')}
              </Typography>

              <FieldDetails title={t('Percentage of Media')} value={toPercentString(platformShare)} />
            </>
          )}
        </>
      )}
    </>
  );
};
